import { isEmptyObject, chunk } from '@seekingalpha/samw-utils';

import type { ObjectT } from 'GlobalShared/types/common';

type ParamsT = Record<string, ReadonlyArray<string | number>>;

type ChunkedRequestResponseT = Promise<{
  data: ReadonlyArray<ObjectT>;
  included: ReadonlyArray<ObjectT>;
}>;

const MAX_PARAMS_PER_REQUEST = 100;

const getParamsArray = (groupedChunks: ParamsT): ReadonlyArray<ParamsT> => {
  const { params, nextParams } = Object.keys(groupedChunks).reduce(
    (accumulator, key) => {
      const [firstChunk, ...chunks] = groupedChunks[key];

      // @ts-expect-error ImplicitAny
      accumulator.params[key] = firstChunk;

      if (chunks.length) {
        // @ts-expect-error ImplicitAny
        accumulator.nextParams[key] = chunks;
      }

      return accumulator;
    },
    {
      params: {},
      nextParams: {},
    },
  );

  return isEmptyObject(nextParams)
    ? [params]
    : [params].concat(getParamsArray(nextParams));
};

const getGroupedChunks = (
  requestParams: ParamsT,
  limitRequestParams: number,
): ReadonlyArray<ParamsT> => {
  const groupedChunks = Object.keys(requestParams).reduce<Record<string, any>>(
    (accumulator, key) => {
      accumulator[key] = chunk(requestParams[key], limitRequestParams);

      return accumulator;
    },
    {},
  );

  return getParamsArray(groupedChunks);
};

export const makeChunkedRequest = (
  request: (arg1?: any) => Promise<any>,
  requestParams: ReadonlyArray<any> | ParamsT,
  limitRequestParams: number = MAX_PARAMS_PER_REQUEST,
): ChunkedRequestResponseT => {
  // @ts-expect-error
  const chunks = Array.isArray(requestParams) ? chunk(requestParams, limitRequestParams) : getGroupedChunks(requestParams, limitRequestParams);

  const requests = chunks.map((resourceChunk) => {
    return request(resourceChunk);
  });

  return Promise.all(requests).then((responses) => {
    return responses.filter(Boolean).reduce(
      (acc, { data = [], included = [] }) => {
        return {
          ...acc,
          data: [...acc.data, ...data],
          included: [...acc.included, ...included],
        };
      },
      {
        data: [],
        included: [],
      },
    );
  });
};
